/**
 * Rig-preview control state (ENG-946).
 *
 * Module singleton — read every frame by HeadModel (GLB morphs), OGSModel
 * (splat rig) and StreamAvatarModel (stream rig) when the face graph's
 * `rigController` node is active (the `__rigcontrol__` sentinel), so this must
 * NEVER move into React state. The panel (RigPreviewPanel) writes it at UI
 * cadence; the render loop only reads.
 *
 * Two modes, selected by the rigController NODE's data — the blueprint owns
 * the routing, this module only holds the values:
 *   passthrough = false (pose): the composed frame IS the slider pose.
 *   passthrough = true:         the live A2F ARKit stream flows through, and
 *                               channels the user locked are overridden by
 *                               their slider value.
 *
 * With no such node in the graph the panel arms `direct` instead (ENG-1027) and
 * the same composed pose reaches the face at full weight — the blueprint is the
 * only thing missing, never the ARKit->MetaHuman mapping. rigControlWeight()
 * below is the single place that resolves node-vs-direct.
 */
// Explicit .js extension: this module is imported directly by node:test, which
// (unlike Vite) does not resolve extension-less specifiers.
import { ARKIT_NAMES } from './arkitNames.js'

export const rigControlState = {
  /** Slider-authored pose, one entry per ARKit channel. */
  arkit: new Float32Array(52),
  /** 1 = this slider overrides the live stream while in passthrough. */
  locked: new Uint8Array(52),
  /** Mirrored from the rigController node's data on every face-graph eval. */
  passthrough: false,
  /**
   * Raw-rig board armed (ENG-946). While true the Rig Preview panel writes the
   * bundle's own MetaHuman control vector straight to the splat, and the ARKit
   * path (OGSModel.driveSplatFace) stands down so there is ONE writer per frame.
   * Splat characters only — a GLB rig has no such control space.
   */
  rawMode: false,
  /**
   * Direct drive armed (ENG-1027). True while the Rig Preview panel is driving
   * the face WITHOUT a rigController node in the graph. The ARKit->MetaHuman
   * mapping is ours end to end (ogs/rig/arkitToMh.js -> rigGatherFromNames ->
   * arkitToIsaacRig), so a character whose face graph has no such node is not a
   * reason for the sliders to do nothing — it only means no blueprint is
   * authoring the routing, and the panel takes it at full weight instead.
   * A wired node always wins: see rigControlWeight below.
   */
  direct: false,
  /** Canonical channel order, so consumers/tests never re-derive it. */
  names: ARKIT_NAMES,
}

/**
 * How much of the composed slider frame reaches the face this frame.
 *
 * Lives here rather than at the three call sites (OGSModel splat rig, HeadModel
 * GLB morphs, StreamAvatarModel stream rig) so the precedence rule is stated
 * once. The graph outranks the panel: a blueprint that blends the sliders down
 * to 0.25 keeps that authored weight even while the panel is armed, and only a
 * graph with NO rigController hands control to `direct`.
 *
 * @param {Record<string, number>|null|undefined} faceWeights evaluateGraph output
 * @returns {number} 0..1
 */
export function rigControlWeight(faceWeights) {
  const node = Math.min(faceWeights?.['__rigcontrol__'] ?? 0, 1)
  if (node > 0) return node
  return rigControlState.direct ? 1 : 0
}

/**
 * Arm/disarm direct drive (ENG-1027). Arming also pins passthrough off: that
 * flag is authored on the rigController NODE and mirrored by GraphEvaluator, so
 * with no node there is nothing to author it and it must not keep a stale value
 * from a previously loaded blueprint.
 */
export function setRigDirect(on) {
  rigControlState.direct = !!on
  if (on) rigControlState.passthrough = false
}

// One scratch frame reused every call — the render loop calls this per frame
// and must not allocate (CLAUDE.md hot-path rule). Callers must not retain the
// returned array across frames.
const _scratch = new Float32Array(52)

/**
 * Build the ARKit-52 frame this controller contributes.
 * @param {Float32Array|null} liveArkit the live A2F frame (getCurrentArkit())
 * @returns {Float32Array} module scratch — do not retain across frames
 */
export function composeRigControlArkit(liveArkit) {
  const { arkit, locked, passthrough } = rigControlState
  if (!passthrough) {
    _scratch.set(arkit)
    return _scratch
  }
  for (let i = 0; i < 52; i++) {
    _scratch[i] = locked[i] ? arkit[i] : (liveArkit ? (liveArkit[i] ?? 0) : 0)
  }
  return _scratch
}

export function setRigChannel(index, value) {
  if (index >= 0 && index < 52) rigControlState.arkit[index] = value
}

export function setRigLock(index, on) {
  if (index >= 0 && index < 52) rigControlState.locked[index] = on ? 1 : 0
}

/** Set channels by ARKit name — accepts camelCase ('jawOpen') or Pascal ('JawOpen'). */
export function setRigPose(values) {
  if (!values) return
  for (let i = 0; i < ARKIT_NAMES.length; i++) {
    const pascal = ARKIT_NAMES[i]
    const camel = pascal.charAt(0).toLowerCase() + pascal.slice(1)
    if (values[camel] !== undefined) rigControlState.arkit[i] = values[camel]
    else if (values[pascal] !== undefined) rigControlState.arkit[i] = values[pascal]
  }
}

export function resetRigControl() {
  rigControlState.arkit.fill(0)
  rigControlState.locked.fill(0)
  rigControlState.passthrough = false
  rigControlState.rawMode = false
  rigControlState.direct = false
}

if (typeof window !== 'undefined') {
  window.__RIG_CONTROL__ = {
    state: rigControlState,
    setChannel: setRigChannel,
    setLock: setRigLock,
    setPose: setRigPose,
    setDirect: setRigDirect,
    reset: resetRigControl,
    compose: composeRigControlArkit,
    weight: rigControlWeight,
  }
}
